import { create } from "zustand";
import { de, type MessageKey } from "./de.js";
import { en } from "./en.js";
import { formatMessage } from "./messages.js";

export type Locale = "de" | "en";
export type { MessageKey };

const DICTS: Record<Locale, Record<MessageKey, string>> = { de, en };
const warned = new Set<string>();

interface LocaleState {
  locale: Locale;
  setLocale(l: Locale): void;
}

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: detectLocale(),
  setLocale: (locale) => {
    try {
      localStorage.setItem("zend.locale", locale);
    } catch {
      /* private mode */
    }
    if (typeof document !== "undefined") document.documentElement.lang = locale;
    set({ locale });
  },
}));

function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem("zend.locale");
    if (stored === "de" || stored === "en") return stored;
  } catch {
    /* no storage */
  }
  if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("en")) return "en";
  return "de";
}

export function setLocale(locale: Locale): void {
  useLocaleStore.getState().setLocale(locale);
}

export function getLocale(): Locale {
  return useLocaleStore.getState().locale;
}

/** Translate an ICU message; unknown keys return the key itself (and warn once in dev). */
export function t(key: MessageKey | string, vars?: Record<string, string | number>): string {
  const locale = useLocaleStore.getState().locale;
  const text: string | undefined = DICTS[locale][key as MessageKey] ?? DICTS.de[key as MessageKey];
  if (text === undefined) {
    if (!warned.has(key)) {
      warned.add(key);
      if (import.meta.env?.DEV) console.warn(`[i18n] missing key ${key}`);
    }
    return key;
  }
  return formatMessage(text, locale, vars);
}

/** Hook: re-renders on locale change and returns the translate function. */
export function useT(): typeof t {
  useLocaleStore((s) => s.locale);
  return t;
}

/** Picks the localized field of a content object ({ de, en }). */
export function pick(text: { de: string; en: string }): string {
  return text[useLocaleStore.getState().locale];
}
